import toast from "react-hot-toast";
import { useDownloadSolverInputs } from "../api/use-export";

interface SolverInputExportButtonProps {
  scheduleId: string;
}

/**
 * Header toolbar button that downloads the schedule's solver-input workbook
 * (.xlsx). Visually mirrors {@link ExportToolbarButton} so the toolbar reads as
 * a single row of equivalent actions.
 *
 * Unlike the run-scoped Export modal, this action is **not** run-gated — the
 * backend returns a valid (possibly empty) workbook for any existing schedule,
 * so the button stays enabled even before the first scheduler run. The schedule
 * home page is responsible for gating *mount* on the
 * `canManageScheduleMasterData` role (mirroring the backend `OrgAdminOrAbove`
 * policy); the backend enforces the real check.
 *
 * Double-click guard is concrete: the button is `disabled` while pending AND the
 * click handler early-returns when a download is already in flight, since
 * `useMutation` alone does not prevent concurrent `mutate()` calls. Errors
 * surface through the shared error path as a toast (never a downloaded .xlsx
 * containing JSON); a mid-session 403 shows an actionable permission message.
 */
export function SolverInputExportButton({
  scheduleId,
}: SolverInputExportButtonProps) {
  const mutation = useDownloadSolverInputs(scheduleId);
  const isPending = mutation.isPending;

  const handleClick = () => {
    // Belt-and-suspenders: `disabled` prevents the click in normal DOM flow,
    // but an early return also blocks a programmatic / rapid second mutate().
    if (isPending) return;
    mutation.mutate(undefined, {
      onError: (error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to download solver inputs.",
        );
      },
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      data-testid="schedule-solver-inputs-button"
      aria-label={
        isPending ? "Downloading solver inputs" : "Download solver inputs"
      }
      aria-busy={isPending}
      className="bg-surface-2 text-text-soft rounded-md px-[14px] py-[7px] font-medium text-[13px] cursor-pointer flex items-center gap-1.5 hover:bg-surface-2/80 disabled:opacity-60 disabled:cursor-wait"
    >
      {isPending ? (
        <SpinnerIcon />
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <path d="M7 10l5 5 5-5" />
          <path d="M12 15V3" />
        </svg>
      )}
      {isPending ? "Preparing…" : "Solver inputs"}
    </button>
  );
}

/**
 * Indeterminate spinner glyph shown while the download is in flight. CSS-only
 * spin, matching the design system's other loading affordances (see
 * `RunSchedulerButton`).
 */
function SpinnerIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-testid="schedule-solver-inputs-spinner"
      className="animate-spin"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
